import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { JiraProjectMapping } from '../components/JiraProjectMapping';
import { JiraSyncSection } from '../components/JiraSyncSection';
import { JiraBoard } from '../components/JiraBoard';
import * as projectService from '../services/projectService';

export function JiraIntegration(): React.ReactElement | null {
  const { projectId } = useParams<{ projectId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();

  const [project, setProject] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadProject();
  }, [projectId]);

  const loadProject = async () => {
    if (!token || !projectId) return;

    try {
      setIsLoading(true);
      const projectData = await projectService.getProjectById(projectId, token);
      setProject(projectData);
    } catch (err: any) {
      setError(err.message || 'Failed to load project');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-gray-500 dark:text-gray-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  if (error || !project) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
          <div className="text-red-600 dark:text-red-400">{error || 'Project not found'}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="bg-gradient-to-r from-indigo-600 via-blue-600 to-blue-400 rounded-lg shadow-lg mb-6 p-6">
            <div className="flex justify-between items-center">
              <div>
                <button
                  onClick={() => navigate(`/projects/${projectId}`)}
                  className="text-sm text-blue-100 hover:text-white mb-2 inline-flex items-center transition-colors"
                >
                  ← Back to Project
                </button>
                <h1 className="text-3xl font-bold text-white">
                  JIRA Info
                </h1>
                <p className="text-blue-100 mt-1">{project.name}</p>
              </div>
              <div className="flex items-center space-x-3">
                <svg className="w-12 h-12 text-white opacity-80" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.571 11.513H0a5.218 5.218 0 0 0 5.232 5.215h2.13v2.057A5.215 5.215 0 0 0 12.575 24V12.518a1.005 1.005 0 0 0-1.005-1.005zm5.723-5.756H5.736a5.215 5.215 0 0 0 5.215 5.214h2.129v2.058a5.218 5.218 0 0 0 5.215 5.214V6.758a1.001 1.001 0 0 0-1.001-1.001zM23.013 0H11.455a5.215 5.215 0 0 0 5.215 5.215h2.129v2.057A5.215 5.215 0 0 0 24 2.057V1.001A1.001 1.001 0 0 0 23.013 0z"/>
                </svg>
              </div>
            </div>
          </div>

          {/* Main Content - 2/3 + 1/3 Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - JIRA Board (2/3 width) */}
            <div className="lg:col-span-2">
              <JiraBoard projectId={projectId!} />
            </div>

            {/* Right Column - Configuration & Sync (1/3 width) */}
            <div className="lg:col-span-1 space-y-6">
              {/* JIRA Project Mapping */}
              <JiraProjectMapping projectId={projectId!} />

              {/* JIRA Requirements Sync */}
              <JiraSyncSection projectId={projectId!} />

              {/* JIRA Sync Statistics */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4">JIRA Sync Statistics</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  More JIRA tracking information will be displayed here in the future.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
